import { useFormik } from "formik";
import React from "react";
import ArticleEditor from "../components/articleEditor";
import "../add/style.css";
import Input from "../../../components/input";
import { AddEditArticleSchema } from "../../../utils/validationSchema";
import { FormHelperText, useTheme } from "@mui/material";
import toast from "react-hot-toast";
import {
  AllowedImageTypes,
  ARTICLE_FIELD_LIMITS,
  ARTICLE_STATUS_OPTIONS,
} from "../../../utils/contentTypes";
import { BiErrorAlt } from "react-icons/bi";
import TagInput from "./tagInput";
import StatusSelect from "../../../components/statusSelect";

const ArticleForm = ({
  title,
  description,
  submitLabel,
  initialContent = "",
  initialBannerPreview = "",
  initialTitle = "",
  initialReadTime = "5",
  submitHandler,
  initialDescription = "",
  initialStatus = "",
  initialTags = [],
}) => {
  const theme = useTheme();
  const imageRef = React.useRef(null);
  const objectUrlRef = React.useRef(null);
  const editorRef = React.useRef(null);
  const [bannerPreview, setBannerPreview] =
    React.useState(initialBannerPreview);
  const MAX_IMAGE_SIZE_MB = 2;

  const articleForm = useFormik({
    initialValues: {
      banner: null,
      content: initialContent,
      title: initialTitle,
      readTime: initialReadTime,
      description: initialDescription,
      status: initialStatus || "",
      tags: initialTags || [],
    },
    enableReinitialize: true,
    validationSchema: () => AddEditArticleSchema(bannerPreview),
    onSubmit: async (values) => {
      const persisted = await editorRef.current?.persistLocalMedia?.();
      const content = persisted?.content ?? values.content;
      const removedMediaUrls = persisted?.removedMediaUrls ?? [];

      await submitHandler({
        ...values,
        content,
        removedMediaUrls,
      });
    },
  });

  const imageHandler = () => {
    imageRef.current?.click();
  };

  const handleReadTimeChange = (event) => {
    const raw = event.target.value;

    if (raw === "") {
      articleForm.setFieldValue("readTime", "");
      return;
    }

    const digits = raw.replace(/\D/g, "").slice(0, 2);
    if (!digits) return;

    const capped = Math.min(
      parseInt(digits, 10),
      ARTICLE_FIELD_LIMITS.READ_TIME_MAX,
    );
    articleForm.setFieldValue("readTime", String(capped));

    console.log("[ArticleForm] readTime capped:", capped);
  };

  const imageChangeHandler = (event) => {
    const image = event.currentTarget.files?.[0] ?? null;

    if (image && image.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      toast.error(`Image size should not exceed ${MAX_IMAGE_SIZE_MB} MB`);
      event.currentTarget.value = "";
      return;
    }

    articleForm.setFieldValue("banner", image);
  };

  React.useLayoutEffect(() => {
    let nextPreview;

    if (articleForm.values.banner) {
      nextPreview = URL.createObjectURL(articleForm.values.banner);
    } else {
      nextPreview = initialBannerPreview;
    }

    if (objectUrlRef.current && articleForm.values.banner) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    objectUrlRef.current = nextPreview;
    setBannerPreview(nextPreview);

    return () => {
      if (articleForm.values.banner && objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, [articleForm.values.banner, initialBannerPreview]);

  return (
    <div className="article-page">
      <div className="article-form">
        {/* Sticky header with title + save button */}
        <div className="article-form__header">
          <div>
            <h2>{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <button
            type="submit"
            className="article-actions__primary"
            onClick={articleForm.handleSubmit}
            disabled={articleForm.isSubmitting}
          >
            {articleForm.isSubmitting ? "Saving..." : submitLabel}
          </button>
        </div>

        <div className="article-card">
          <div className="article-grid">
            {/* LEFT COLUMN */}
            <div className="article-grid__left">
              <div className="content-block">
                <Input
                  label="Title"
                  name="title"
                  placeholder="Enter article title"
                  value={articleForm.values.title}
                  errors={articleForm.errors}
                  touched={articleForm.touched}
                  handleChange={articleForm.handleChange}
                  handleOnBlur={articleForm.handleBlur}
                  maxLength={ARTICLE_FIELD_LIMITS.TITLE_MAX}
                />
              </div>

              <div className="content-block">
                <Input
                  label="Short description"
                  name="description"
                  placeholder="Brief summary (for listing previews)"
                  value={articleForm.values.description}
                  errors={articleForm.errors}
                  touched={articleForm.touched}
                  handleChange={articleForm.handleChange}
                  handleOnBlur={articleForm.handleBlur}
                  rows={3}
                  type="textarea"
                  maxLength={ARTICLE_FIELD_LIMITS.DESCRIPTION_MAX}
                />
              </div>

              {/* Banner picker */}
              <div className="content-block">
                <div className="banner-row">
                  <div className="banner-row__controls">
                    <input
                      style={{ display: "none" }}
                      ref={imageRef}
                      type="file"
                      accept={AllowedImageTypes}
                      onChange={imageChangeHandler}
                    />

                    <button
                      type="button"
                      className="banner-picker__button btn-sm"
                      onClick={imageHandler}
                    >
                      Choose image
                      {/* {bannerPreview ? "Replace banner" : "Upload banner"} */}
                    </button>

                    <div className="banner-row__hint">
                      Recommended: 1200×628px · JPG/PNG · max{" "}
                      {MAX_IMAGE_SIZE_MB}MB
                    </div>
                  </div>

                  <div className="banner-row__preview">
                    {bannerPreview ? (
                      <div className="banner-preview">
                        <img
                          src={bannerPreview}
                          alt="Selected banner preview"
                        />
                      </div>
                    ) : (
                      <div
                        className={`banner-preview banner-preview--empty ${
                          articleForm.touched.banner &&
                          articleForm.errors.banner
                            ? "banner-preview--error"
                            : ""
                        }`}
                      >
                        No feature image selected
                      </div>
                    )}

                    {articleForm.touched.banner && articleForm.errors.banner ? (
                      <FormHelperText
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          mt: "6px",
                          color: theme.palette.error.main,
                          fontSize: 13,
                        }}
                      >
                        <BiErrorAlt fontSize="small" />
                        {articleForm.errors.banner}
                      </FormHelperText>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Status / Read time */}
              <div className="article-meta-row content-block">
                <div className="article-meta-item">
                  <StatusSelect
                    label="Status"
                    value={articleForm.values.status}
                    options={ARTICLE_STATUS_OPTIONS}
                    placeholder="Select Status"
                    onChange={(status) =>
                      articleForm.setFieldValue("status", status)
                    }
                    onBlur={() => articleForm.setFieldTouched("status", true)}
                    error={
                      articleForm.touched.status && articleForm.errors.status
                        ? articleForm.errors.status
                        : ""
                    }
                  />
                </div>

                <div className="article-meta-item">
                  <Input
                    label="Read Time (minutes)"
                    name="readTime"
                    placeholder="5"
                    value={articleForm.values.readTime}
                    errors={articleForm.errors}
                    touched={articleForm.touched}
                    handleChange={handleReadTimeChange}
                    handleOnBlur={articleForm.handleBlur}
                    type="number"
                    inputMode="numeric"
                  />
                </div>
              </div>

              {/* Tags */}
              <div className="content-block">
                <TagInput
                  onChange={(tags) => {
                    articleForm.setFieldValue("tags", tags);
                    articleForm.setFieldTouched("tags", true, false);
                  }}
                  tags={articleForm.values.tags || []}
                  errors={articleForm.errors}
                  touched={articleForm.touched}
                />
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="article-grid__right">
              <div className="content-block">
                <label className="field-label">Content</label>
                <ArticleEditor
                  ref={editorRef}
                  error={Boolean(
                    articleForm.touched.content && articleForm.errors.content,
                  )}
                  value={articleForm.values.content}
                  onChange={(value) =>
                    articleForm.setFieldValue("content", value)
                  }
                  maxLength={ARTICLE_FIELD_LIMITS.CONTENT_MAX}
                />
                {articleForm.touched.content && articleForm.errors.content ? (
                  <FormHelperText
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      mt: "4px",
                      color: theme.palette.error.main,
                    }}
                  >
                    <BiErrorAlt fontSize="small" />
                    {articleForm.errors.content}
                  </FormHelperText>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleForm;
